// 치과 「오늘」 — 작업 공정 월별 캘린더 (2026-09-29 대표 지시: "작업공정은 기본이 캘린더 월별로,
//  날짜를 클릭하면 환자목록·치식·공정 정보가 모달 카드로, 공간 부족하면 스크롤") — 모달은 ProcessDayModal(기공소 캘린더와 공용)
// 표시 전용 — 무접촉 공정 흐름(기획서 §8) 그대로: 상태 변경 버튼 없음. 날짜 축 = 도착 예정일(dueDate → deliveryDate 폴백).
// 모집단은 ClinicToday가 읽은 orders(createdAt 90일 창) 그대로 — 여기서 Firestore를 읽지 않는다.
// 스캔만 있는 건·기공소 감지 케이스는 납기가 없으므로 캘린더에 올리지 않는다(ClinicToday 요약 줄에서 접어 유지).
// 2026-10-12 대표 "권고대로"(덴클 1.3.0 공정 3단계): 치과도 「의뢰서 확인 · 디자인 완료 · 포장 완료」 3단계 — clinicStageOf(shared).
//  (구 2026-10-07 치과 2상태 「디자인 완료/완료/진행 중」을 대체.) 확인 전·취소 = 칩 없음(회색 점만).
//  납기 자정 서버 자동 완료(auto_due, 포장 체크 없음) = '도착 예정일 지남'(회색) — 도착·받음 단정 금지(09-29 법무).
import React, { useState, useEffect, useMemo } from 'react';
import { clinicStageOf, STAGE3_KEYS, STAGE3_LABELS, STAGE3_COLOR } from '../utils/shared/clinic-stage';
import ProcessDayModal, { teethLinesOf, dueOf } from './ProcessDayModal';   // 그날 환자 모달·치식 줄 — 기공소 캘린더와 공용 (2026-09-29)

// teethLinesOf는 ProcessDayModal로 옮겼다(공용). 기존 import 경로 호환용 재수출.
export { teethLinesOf };

const DAY = 24 * 3600 * 1000;
// 단계 색 — 공정 3단계(shared STAGE3_COLOR: 의뢰서 확인 남색 / 디자인 완료 초록 / 포장 완료 진회색). 확인 전·도착 예정일 지남 = 회색
const NONE_COLOR = '#94a3b8';
// 셀 안 정렬 순서 — 확인 전(칩 없음) → 의뢰서 확인 → 디자인 완료 → 포장 완료
const STAGE_ORDER = { none: 0, confirmed: 1, designDone: 2, packed: 3 };
// 기공소명 축약 — 접미사만 떼고 나머지는 CSS 말줄임. 떼고 비면 원문 유지
const shortLab = (name) => {
    const s = (name || '').toString().trim();
    const cut = s.replace(/(치과기공소|기공소)$/, '').trim();
    return cut || s;
};

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const arriveOf = (o) => dueOf(o);

// 캘린더용 건 판정 — clinicStageOf(3단계). 포장 완료·도착 예정일 지남은 흐리게(done) 표시
//  st.label: '의뢰서 확인'/'디자인 완료'/'포장 완료'/'도착 예정일 지남'/''(칩 없음)
const entryOf = (o) => {
    const raw = clinicStageOf(o);
    // 칩 없는 건(확인 전)의 스캔 상태 보조 문구는 scans를 읽지 않는 이 화면에선 '확인 중…'으로 굳으므로 쓰지 않는다
    const st = raw.label ? raw : { ...raw, detail: null };
    const done = st.key === 'packed' || st.autoDue;
    const color = st.autoDue ? NONE_COLOR : (st.key ? STAGE3_COLOR[st.key] : NONE_COLOR);
    return { o, st, done, bucket: st.key || 'none', color };
};

function ClinicProcessCalendar({ orders, qNorm, onView, onPreview, escDisabled = false }) {   // escDisabled: 3D 뷰어 열림 시 모달 Esc 무시 (2026-09-29 검토 P2-2)
    // 환자명 검색(ClinicToday qText 정규화값) — 캘린더 칸·모달 모두 적용
    const hitQ = (name) => !qNorm || (name || '').toString().toLowerCase().replace(/\s/g, '').includes(qNorm);
    const now = new Date();
    // 항상 이번 달로 열린다 — 본 달을 저장하지 않는다(사양 6)
    const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() });
    const [pickedDay, setPickedDay] = useState(null);   // 클릭한 날짜의 자정 ms
    const [vw, setVw] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
    useEffect(() => {
        const onR = () => setVw(window.innerWidth);
        window.addEventListener('resize', onR);
        return () => window.removeEventListener('resize', onR);
    }, []);
    const phone = vw < 640;
    const namesPerCell = phone ? 1 : vw < 960 ? 2 : 3;

    // 날짜(자정 ms) → 건 목록. 납기 없는 건은 캘린더에 올리지 않는다(ClinicToday '도착일을 안 적은 건' 카드가 담당)
    const byDay = useMemo(() => {
        const map = {};
        (orders || []).forEach(o => {
            const a = arriveOf(o);
            if (!a) return;
            if (!hitQ(o.patientName)) return;
            const k = startOfDay(a).getTime();
            (map[k] = map[k] || []).push(entryOf(o));
        });
        Object.values(map).forEach(list => list.sort((a, b) =>
            (a.done - b.done) || ((STAGE_ORDER[a.bucket] ?? 9) - (STAGE_ORDER[b.bucket] ?? 9)) || (a.o.patientName || '').localeCompare(b.o.patientName || '', 'ko')));
        return map;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orders, qNorm]);

    // 월간 그리드(월~일)
    const cells = useMemo(() => {
        const first = new Date(ym.y, ym.m, 1);
        const offset = (first.getDay() + 6) % 7;   // 월요일 시작
        const start = first.getTime() - offset * DAY;
        const lastDate = new Date(ym.y, ym.m + 1, 0).getDate();
        const rows = Math.ceil((offset + lastDate) / 7);
        const out = [];
        for (let i = 0; i < rows * 7; i++) {
            const d = new Date(start + i * DAY);
            d.setHours(0, 0, 0, 0);   // DST 영향 방지(한국은 없음) 겸 자정 고정
            out.push({ key: d.getTime(), date: d, inMonth: d.getMonth() === ym.m });
        }
        return out;
    }, [ym]);

    const todayKey = startOfDay(new Date()).getTime();
    const goMonth = (delta) => setYm(({ y, m }) => { const d = new Date(y, m + delta, 1); return { y: d.getFullYear(), m: d.getMonth() }; });
    const goThisMonth = () => { const d = new Date(); setYm({ y: d.getFullYear(), m: d.getMonth() }); };

    // 모달(Esc·바깥 클릭·스크롤 잠금·폰 전체화면)은 ProcessDayModal이 맡는다
    const picked = pickedDay !== null ? (byDay[pickedDay] || []) : [];
    const pickedDate = pickedDay !== null ? new Date(pickedDay) : null;
    const partnerNameOf = (o) => o.toUserName || o.internalLabName || '기공소';

    return (
        <div>
            <div style={C.nav}>
                <button onClick={() => goMonth(-1)} style={C.navBtn} aria-label="지난 달">{'<'}</button>
                <span style={C.navTitle}>{ym.y}년 {ym.m + 1}월</span>
                <button onClick={() => goMonth(1)} style={C.navBtn} aria-label="다음 달">{'>'}</button>
                <button onClick={goThisMonth} style={C.navThis}>이번 달</button>
                <span style={C.legend}>
                    {STAGE3_KEYS.map(k => (
                        <span key={k} style={C.legendItem}><span style={{ ...C.dot, background: STAGE3_COLOR[k] }} />{STAGE3_LABELS[k]}</span>
                    ))}
                </span>
            </div>

            <div style={C.weekRow}>
                {['월', '화', '수', '목', '금', '토', '일'].map((w, i) => (
                    <div key={w} style={{ ...C.weekCell, color: i === 5 ? '#2563eb' : i === 6 ? '#dc2626' : '#64748b' }}>{w}</div>
                ))}
            </div>
            <div style={C.grid}>
                {cells.map(c => {
                    const list = byDay[c.key] || [];
                    const n = list.length;
                    const isToday = c.key === todayKey;
                    const dow = c.date.getDay();
                    const shown = list.slice(0, namesPerCell);
                    const more = n - shown.length;
                    return (
                        <div key={c.key} role={n ? 'button' : undefined} tabIndex={n ? 0 : -1}
                            onClick={() => { if (n) setPickedDay(c.key); }}
                            onKeyDown={(e) => { if (n && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setPickedDay(c.key); } }}
                            title={n ? `${c.date.getMonth() + 1}월 ${c.date.getDate()}일 도착 예정 ${n}건 — 눌러서 환자별 상세` : undefined}
                            style={{
                                ...C.cell,
                                ...(phone ? { minHeight: 54, padding: '4px 4px' } : {}),
                                ...(c.inMonth ? {} : C.cellOut),
                                ...(isToday ? C.cellToday : {}),
                                ...(n ? { cursor: 'pointer' } : {}),
                            }}>
                            <div style={C.cellHead}>
                                <span style={{ ...C.dayNum, ...(isToday ? C.dayNumToday : {}), color: isToday ? '#fff' : !c.inMonth ? '#cbd5e1' : dow === 0 ? '#dc2626' : dow === 6 ? '#2563eb' : '#334155' }}>{c.date.getDate()}</span>
                                {n > 0 && <span style={C.countBadge}>{n}</span>}
                            </div>
                            {shown.map(e => {
                                const lab = shortLab(e.o.toUserName || e.o.internalLabName || '');
                                const has3d = !!onPreview && (e.o.designPreview?.files || []).length > 0;
                                // 3단계 배지(2026-10-12) — 확인 전·취소는 배지 없음(label ''), auto_due는 '도착 예정일 지남'
                                const short = e.st.label;
                                return (
                                    <div key={e.o.id} style={{ ...C.name, color: e.done ? '#94a3b8' : '#0f172a' }}
                                        title={`${e.o.patientName || '환자명 없음'}${lab ? ' · ' + lab : ''} · ${e.st.label || '기공소 확인 전'}`}>
                                        <span style={{ ...C.dot, background: e.color }} />
                                        <span style={C.nameText}>{e.o.patientName || '환자명 없음'}</span>
                                        {/* 폰은 환자명+색 점만. 그 위 폭에서는 기공소명(말줄임)+상태 배지+3D 버튼 */}
                                        {!phone && lab && <span style={C.labText}>{'· ' + lab}</span>}
                                        {!phone && short && <span style={{ ...C.stageChip, color: e.color, borderColor: e.color + '66', background: e.color + '14' }}>{short}</span>}
                                        {!phone && has3d && (
                                            <button onClick={(ev) => { ev.stopPropagation(); onPreview(e.o); }}
                                                onKeyDown={(ev) => ev.stopPropagation()}
                                                style={C.btn3dMini} title="3D 디자인 미리보기">3D</button>
                                        )}
                                    </div>
                                );
                            })}
                            {more > 0 && <div style={C.more}>+{more}</div>}
                        </div>
                    );
                })}
            </div>
            <p style={C.foot}>{'도착 예정일 기준입니다. 「포장 완료」는 기공소가 포장을 마쳤다는 표시이며, 도착 여부는 기공소와 확인해 주세요.'}</p>

            {/* 그날 환자 모달 — 치과는 상태 변경 버튼 없음(무접촉): actionsOf 미지정. simple=치과 모드(보조 '디자인완료' 칩 숨김 — 3단계 라벨과 겹침) */}
            <ProcessDayModal
                simple
                date={pickedDate}
                entries={picked.map(e => ({ key: e.o.id, o: e.o, st: e.st, color: e.color, done: e.done }))}
                partnerNameOf={partnerNameOf}
                dueLabel="도착 예정"
                onClose={() => setPickedDay(null)}
                onOpen={(o) => onView(o.id)}
                onPreview={onPreview}
                escDisabled={escDisabled}
            />
        </div>
    );
}

const C = {
    nav: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' },
    navBtn: { width: 30, height: 30, borderRadius: 7, border: '1px solid #e2e8f0', background: '#fff', color: '#334155', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
    navTitle: { fontSize: 15, fontWeight: 800, color: '#0f172a', minWidth: 96, textAlign: 'center' },
    navThis: { padding: '6px 11px', borderRadius: 7, border: '1px solid #e2e8f0', background: '#fff', color: '#334155', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
    legend: { marginLeft: 'auto', display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: 11.5, color: '#64748b' },
    legendItem: { display: 'inline-flex', alignItems: 'center', gap: 4 },
    dot: { width: 6, height: 6, borderRadius: '50%', flexShrink: 0, display: 'inline-block' },

    weekRow: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 4 },
    weekCell: { fontSize: 11, fontWeight: 700, textAlign: 'center', padding: '4px 0' },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 },
    cell: { minHeight: 84, border: '1px solid #e2e8f0', borderRadius: 8, padding: '6px 7px', background: '#fff', display: 'flex', flexDirection: 'column', gap: 3, overflow: 'hidden', minWidth: 0 },
    cellOut: { background: '#fafbfc', borderColor: '#f1f5f9' },
    cellToday: { borderColor: '#6366f1', boxShadow: '0 0 0 1px #6366f1 inset', background: '#fbfbff' },
    cellHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 },
    dayNum: { fontSize: 12, fontWeight: 700, lineHeight: '18px' },
    dayNumToday: { background: '#6366f1', borderRadius: '50%', width: 20, height: 20, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 },
    countBadge: { fontSize: 10.5, fontWeight: 800, color: '#334155', background: '#f1f5f9', borderRadius: 999, padding: '1px 6px', lineHeight: '14px' },
    name: { display: 'flex', alignItems: 'center', gap: 4, fontSize: 11.5, fontWeight: 600, minWidth: 0 },
    nameText: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0, maxWidth: '55%' },
    labText: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flexShrink: 1, color: '#64748b', fontWeight: 500, fontSize: 10.5 },
    stageChip: { marginLeft: 'auto', flexShrink: 0, fontSize: 9.5, fontWeight: 800, padding: '0 5px', borderRadius: 999, border: '1px solid', lineHeight: '14px', whiteSpace: 'nowrap' },
    btn3dMini: { flexShrink: 0, padding: '0 5px', borderRadius: 5, border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4338ca', fontSize: 9.5, fontWeight: 800, lineHeight: '14px', cursor: 'pointer', fontFamily: 'inherit' },
    more: { fontSize: 10.5, fontWeight: 700, color: '#64748b' },
    foot: { fontSize: 11, color: '#94a3b8', margin: '10px 0 0', lineHeight: 1.5 },

};

export default ClinicProcessCalendar;
